import type {
	Adapter,
	AnalyticsEvents,
	SocialMediaType,
} from "../../AppCommunicator";
import { LinkTypes } from "../../AppCommunicator";
import { sendToFlutter } from "./AdapterUtil";

enum FlutterEvents {
	FIRST_TIME_CLICKED = "firstTimeClicked",
	LOADING_DONE = "loadingDone",
	CHANGE_LOADING = "changeLoading",
	PLAY_REWARDED_AD = "playRewarded",
	ANALYTICS_EVENT = "analyticsEvent",
	BUY_PRODUCT = "buyProduct",
	SET_LANGUAGE = "setLanguage",
	OPEN_STORE = "openStoreReview",
	CAN_VIBRATE = "canVibrate",
	VIBRATE = "vibrate",
	EXIT_APP = "exitApp",
	DOWNLOAD_IMAGE = "downloadImage",
	LINK_ACCOUNT = "linkAccount",
	RESET_GAME = "resetGame",
	RELOG_USER = "relogUser",
	GAME_VERSION = "game_version",
	OPEN_SECOND_WEBVIEW = "openSecondWebview",
	CLOSE_SECOND_WEBVIEW = "closeSecondWebview",
	SHOW_PRIVACY_OPTIONS_FORM_UMP = "showPrivacyOptionsFormUmp",
	SHOW_ERROR_DURING_LOADING = "showErrorDuringLoading",
}

export class FlutterAdapter implements Adapter {
	public firstTimeClicked(): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.FIRST_TIME_CLICKED }),
		);
	}

	public loadingDone(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.LOADING_DONE }));
	}

	public setLoadingProgress(progress: number): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.CHANGE_LOADING,
				loadingValue: progress,
			}),
		);
	}

	public playRewardedAd(): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.PLAY_REWARDED_AD }),
		);
	}

	public sendAnalyticsEvent(
		event: AnalyticsEvents,
		data?: Record<string, number | string>,
	): void {
		const record = data || {};
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.ANALYTICS_EVENT,
				eventName: event,
				...record,
			}),
		);
	}

	public buyProduct(productId: string): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.BUY_PRODUCT, productId }),
		);
	}

	public setLanguage(language: string): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.SET_LANGUAGE, language }),
		);
	}

	public openStore(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.OPEN_STORE }));
	}

	public canVibrate(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.CAN_VIBRATE }));
	}

	public vibrate(vibrationTimeMs: number): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.VIBRATE,
				timeMs: vibrationTimeMs,
			}),
		);
	}

	public exitApp(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.EXIT_APP }));
	}

	public downloadImage(base64: string): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.DOWNLOAD_IMAGE,
				base64Image: base64,
			}),
		);
	}

	public linkAccount(
		type: LinkTypes,
		socialMediaType: SocialMediaType,
	): void {
		if (type === LinkTypes.WITH_EXIST) {
			sendToFlutter(
				JSON.stringify({
					event: FlutterEvents.LINK_ACCOUNT,
					withExist: true,
					socialMediaType: socialMediaType,
				}),
			);
			return;
		} else if (type === LinkTypes.WITH_OVERRIDE) {
			sendToFlutter(
				JSON.stringify({
					event: FlutterEvents.LINK_ACCOUNT,
					withOverride: true,
					socialMediaType: socialMediaType,
				}),
			);
			return;
		} else {
			sendToFlutter(
				JSON.stringify({
					event: FlutterEvents.LINK_ACCOUNT,
					socialMediaType: socialMediaType,
				}),
			);
		}
	}

	public resetGame(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.RESET_GAME }));
	}

	public relogUser(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.RELOG_USER }));
	}

	public sendGameVersion(version: string): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.GAME_VERSION,
				value: version,
			}),
		);
	}

	public openSecondWebview(url: string): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.OPEN_SECOND_WEBVIEW, url }),
		);
	}

	public closeSecondWebview(): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.CLOSE_SECOND_WEBVIEW }),
		);
	}

	public showPrivacyOptionsFormUmp(): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.SHOW_PRIVACY_OPTIONS_FORM_UMP,
			}),
		);
	}

	public showErrorDuringLoading(errorText = "Unknown error"): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.SHOW_ERROR_DURING_LOADING,
				errorText,
			}),
		);
	}
}
